import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import {
  ajvResolver,
  ButtonPrimary,
  ButtonPseudo,
  IconInfo,
  InputText,
  PermissionDenied,
  PermissionEnum,
  Popup,
  ResponseError,
  responseErrorsResolver,
  SidePanel,
  SidePanelType,
  usePrevious,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useCreateClientMutation,
  useLazyGetClientsQuery,
} from './store/service';
import { ClientFormValues } from './types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const scheme = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First Name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last Name is required',
    },
    phone: { type: 'string', minLength: 1, errorMessage: 'Phone is required' },
    email: { type: 'string', minLength: 1, errorMessage: 'E-mail is required' },
  },
  required: ['firstName', 'lastName', 'phone', 'email'],
};

const CreateClient = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [formattedScheme, setFormattedScheme] = useState(scheme);

  const navigate = useNavigate();
  const { search } = useLocation();

  const { activePermissions } = useAppSelector((state) => state.root);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    watch,
  } = useForm<ClientFormValues>({
    resolver: ajvResolver(formattedScheme),
  });

  const email = watch('email');
  const prevEmail = usePrevious(email);

  const [create, { isLoading, isSuccess }] = useCreateClientMutation();
  const [getClients] = useLazyGetClientsQuery();

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);

    setTimeout(() => {
      navigate(`${ROUTES.CLIENTS_LIST}${search || ''}`);
    }, 100);
  };

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const body: ClientFormValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values?.email,
    };

    const response = (await create(body)) as {
      data?: { data: ClientFormValues };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      handleClose();
      getClients(search || '');
    }
  }, [isSuccess, search]);

  useEffect(() => {
    let nextScheme: any = scheme;

    if (email) {
      nextScheme = {
        ...scheme,
        properties: {
          ...scheme.properties,
          email: {
            type: 'string',
            minLength: 1,
            format: 'email',
            errorMessage: 'Email is empty or invalid',
          },
        },
      };
    } else if (!email && prevEmail) {
      nextScheme = scheme;
    }

    setFormattedScheme(nextScheme);
  }, [email, prevEmail]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.USERS_CREATE_CUSTOMER_ACTION)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.createClientForm}>
        <div className={cn.createClientFormInfo}>
          <div className={cn.iconWrapper}>
            <IconInfo />
          </div>
          This phone number will be able to receive a code and sign up while
          public registration is disabled.
        </div>
        <div className={cn.formGroupFlex}>
          <div className={cn.formGroupItem}>
            <Controller
              control={control}
              name="firstName"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  value={value}
                  placeholder="Enter first name..."
                  onChange={onChange}
                  error={errors?.firstName?.message}
                  title="First Name"
                />
              )}
            />
          </div>
          <div className={cn.formGroupItem}>
            <Controller
              control={control}
              name="lastName"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  value={value}
                  placeholder="Enter last name..."
                  onChange={onChange}
                  error={errors?.lastName?.message}
                  title="Last Name"
                />
              )}
            />
          </div>
        </div>
        <div className={cn.formGroup}>
          <Controller
            control={control}
            name="phone"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                value={value}
                placeholder="Enter phone number..."
                onChange={onChange}
                error={errors?.phone?.message}
                title="Phone"
              />
            )}
          />
        </div>
        <div className={cn.formGroup}>
          <Controller
            control={control}
            name="email"
            defaultValue=""
            render={({ field: { onChange, value, name } }) => (
              <InputText
                name={name}
                value={value}
                placeholder="Enter email..."
                onChange={onChange}
                error={errors?.email?.message}
                title="Email"
              />
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <SidePanel
        title="Add user"
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => (isDirty ? setDirtyPopupVisible(true) : handleClose())}
        footer={
          <>
            <ButtonPrimary
              text={isLoading ? 'Loading...' : 'Add'}
              disabled={!isDirty || isLoading}
              onClick={onSubmit}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() =>
                isDirty ? setDirtyPopupVisible(true) : handleClose()
              }
              bold
            />
          </>
        }
        closeOnClickOutside
        sidepanelExpandedKey="client-create-details"
        errorSidepanel={globalError}
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default CreateClient;
